import React, { useState } from 'react';
import { X } from 'lucide-react';
import C from '../../constants/colors';

export default function ZoneFormModal({ zone, zones, sites, preselectedSiteId, preselectedParentZoneId, onSave, onClose }) {
  const isEditing = !!zone?.id;
  const defaultSiteId = zone?.siteId || preselectedSiteId || (sites[0]?.id || '');
  const defaultSite = sites.find(s => s.id === defaultSiteId);
  const [form, setForm] = useState({
    type:        zone?.type || 'FIXED',
    siteId:      defaultSiteId,
    parentZoneId: zone?.parentZoneId || preselectedParentZoneId || '',
    name:        zone?.name || '',
    description: zone?.description || '',
    floor:       zone?.floor ?? '',
    capacity:    zone?.capacity || '',
    lat:         zone?.lat ?? (defaultSite?.lat || ''),
    lng:         zone?.lng ?? (defaultSite?.lng || ''),
  });
  const [errors, setErrors] = useState([]);

  const isMobile = form.type === 'MOBILE';
  const required = ['siteId', 'name', ...(isMobile ? [] : ['floor'])];

  const parentZoneOptions = zones.filter(z =>
    z.siteId === form.siteId &&
    z.id !== zone?.id &&
    (zone == null || z.parentZoneId !== zone.id)
  );

  function handleSubmit() {
    const missing = required.filter(k => String(form[k]).trim() === '');
    if (missing.length) { setErrors(missing); return; }
    const site = sites.find(s => s.id === form.siteId);
    onSave({
      ...(zone || {}),
      type:         form.type,
      siteId:       form.siteId,
      companyId:    site?.companyId || '',
      parentZoneId: form.parentZoneId || null,
      name:         form.name.trim(),
      description:  form.description.trim(),
      capacity:     parseInt(form.capacity) || 0,
      ...(isMobile ? {} : {
        floor: parseInt(form.floor) || 0,
        lat:   parseFloat(form.lat) || 0,
        lng:   parseFloat(form.lng) || 0,
      }),
    });
    onClose();
  }

  const inp = (key, label, ph) => (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '5px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}{required.includes(key) ? ' *' : ''}
      </label>
      <input value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))} placeholder={ph}
        style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none' }} />
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '28px', width: '460px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 700, color: C.textPrimary }}>{isEditing ? 'Edit Zone' : 'Add Zone'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted }}><X size={18} /></button>
        </div>
        {errors.length > 0 && (
          <div style={{ background: C.red + '22', border: `1px solid ${C.red}44`, borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: C.red }}>
            Missing: {errors.join(', ')}
          </div>
        )}

        {/* Zone Type — selector on create, read-only label on edit */}
        <div style={{ marginBottom: '14px' }}>
          <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '5px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Zone Type</label>
          {isEditing ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px' }}>
              <span style={{ fontSize: '13px', color: C.textPrimary }}>{form.type === 'MOBILE' ? 'Mobile' : 'Fixed'}</span>
              <span style={{ fontSize: '11px', color: C.textMuted }}>(cannot be changed)</span>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '8px' }}>
              {['FIXED', 'MOBILE'].map(t => (
                <button key={t} onClick={() => setForm(p => ({ ...p, type: t }))}
                  style={{ flex: 1, padding: '8px', borderRadius: '8px', border: `1px solid ${form.type === t ? C.blue : C.border}`, background: form.type === t ? C.blue + '22' : 'transparent', color: form.type === t ? C.blue : C.textSecondary, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: form.type === t ? 600 : 400 }}>
                  {t === 'FIXED' ? 'Fixed' : 'Mobile'}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Site selector */}
        <div style={{ marginBottom: '14px' }}>
          <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '5px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Site *</label>
          <select value={form.siteId} onChange={e => { const s = sites.find(si => si.id === e.target.value); setForm(p => ({ ...p, siteId: e.target.value, parentZoneId: '', lat: s?.lat || '', lng: s?.lng || '' })); }}
            style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none' }}>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        {/* Parent zone */}
        <div style={{ marginBottom: '14px' }}>
          <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '5px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Parent Zone <span style={{ color: C.textMuted, fontWeight: 400 }}>(optional)</span>
          </label>
          <select value={form.parentZoneId} onChange={e => setForm(p => ({ ...p, parentZoneId: e.target.value }))}
            style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none' }}>
            <option value=''>None (top-level zone)</option>
            {parentZoneOptions.length === 0
              ? <option disabled value='__none__'>— No zones yet for this site —</option>
              : parentZoneOptions.map(z => <option key={z.id} value={z.id}>{z.name}{z.floor != null ? ` (Floor ${z.floor})` : ''}</option>)
            }
          </select>
        </div>

        {inp('name', 'Zone Name', 'e.g. Crew Boat Alpha')}
        {inp('description', 'Description', 'Optional')}

        {inp('capacity', 'Capacity', 'Max headcount')}

        {/* Fixed-only fields */}
        {!isMobile && (
          <>
            {inp('floor', 'Floor Number', '0 = Ground')}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {inp('lat', 'Latitude', 'e.g. 25.2048')}
              {inp('lng', 'Longitude', 'e.g. 55.2708')}
            </div>
          </>
        )}

        {isMobile && (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '12px', color: C.textMuted }}>
            GPS coordinates are set automatically from the site location and updated on each QR scan.
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button onClick={onClose} style={{ padding: '9px 20px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          <button onClick={handleSubmit} style={{ padding: '9px 20px', borderRadius: '8px', border: 'none', background: C.blue, color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Save Zone</button>
        </div>
      </div>
    </div>
  );
}
